import { Link } from '@inertiajs/react'
import Layout from '../Layout'
import { formatCurrency } from '../../lib/format'

interface Account {
  id: number
  name: string
  slug: string
}

interface SaleItem {
  id: number
  quantity: number
  unit_price: number
  discount_cents?: number
  variant_name?: string | null
  product: { id: number; name: string }
}

interface Sale {
  id: number
  subtotal?: number
  total: number
  tax_amount: number
  status: string
  payment_method: string
  notes: string
  created_at: string
  sale_items: SaleItem[]
  customer?: { id: number; name: string }
  user?: { id: number; email_address: string }
}

interface Props {
  sale: Sale
  account: Account
  permissions?: {
    can_manage_sales: boolean
  }
}

export default function SalesShow({ sale, account, permissions }: Props) {
  const paymentLabels: Record<string, string> = {
    cash: 'Efectivo',
    card: 'Tarjeta',
    transfer: 'Transferencia',
    other: 'Otro',
  }

  return (
    <Layout account={account} permissions={permissions}>
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-[#202020]">Venta #{sale.id}</h1>
      </div>

      <div className="bg-white p-6 rounded-lg shadow space-y-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Fecha</p>
            <p className="font-medium">{new Date(sale.created_at).toLocaleString('es-MX')}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Cliente</p>
            <p className="font-medium">{sale.customer?.name || 'General'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Método de pago</p>
            <p className="font-medium">{paymentLabels[sale.payment_method] || sale.payment_method}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Vendedor</p>
            <p className="font-medium">{sale.user?.email_address || '-'}</p>
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-[#202020] mb-2">Productos</h2>
          <div className="overflow-x-auto -mx-6">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Producto</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Cantidad</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Precio unit.</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Subtotal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sale.sale_items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-2">
                      <span className="text-[#202020]">{item.product.name}</span>
                      {item.variant_name && (
                        <span className="block text-xs text-gray-500">— {item.variant_name}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">{item.quantity}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">{formatCurrency(Number(item.unit_price))}</td>
                    <td className="px-4 py-2 text-right font-semibold whitespace-nowrap">
                      {formatCurrency(item.quantity * Number(item.unit_price) - (Number(item.discount_cents) || 0) / 100)}
                      {Number(item.discount_cents) > 0 && (
                        <p className="text-[10px] text-green-600 font-medium">−{formatCurrency((Number(item.discount_cents) || 0) / 100)}</p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="text-right space-y-1">
          <p className="text-sm text-gray-600">Subtotal: {formatCurrency(Number(sale.subtotal || 0))}</p>
          <p className="text-sm text-gray-600">ISV: {formatCurrency(Number(sale.tax_amount || 0))}</p>
          <p className="text-2xl font-bold text-[#55ACEE]">Total: {formatCurrency(Number(sale.total))}</p>
        </div>

        {sale.notes && (
          <div>
            <p className="text-sm text-gray-500">Notas</p>
            <p>{sale.notes}</p>
          </div>
        )}
      </div>

      <div>
        <Link href={`/${account.slug}/sales`} className="text-[#55ACEE] hover:underline">
          ← Volver a ventas
        </Link>
      </div>
    </div>
    </Layout>
  )
}
